class Collection {
  constructor() {
    this.selectors = {
      section: ".facest-filters-section",
      toolbar: ".m-collection-toolbar",
      cols: [".m-toolbar--column-switcher button"],
      productContainer: "[data-product-container]",
      loadMoreBtn: "[data-load-more]",
      variantPaginationNav: "[data-variant-pagination-nav]",
      // Sidebar filters selectors
      sidebar: ".m-sidebar",
      sidebarContent: ".m-sidebar--content",
      openSidebar: ".m-sidebar--open",
      closeSidebar: ".m-sidebar--close",
      filtersWrapper: ".m-filter--wrapper",
    };
    this.mobileSortingSelectors = {
      openSorting: ".m-sortby--open",
      closeSorting: ".m-sortby-mobile--close",
      sortingWrapper: ".m-sortby-mobile--wrapper",
      sortingList: ".m-sortby-mobile--content",
    };
    this.sectionId = "";
    this.isDesignMode = MinimogSettings.design_mode;

    this.currentPage = 1;
    this.paginationType; // "paginate" | "loadmore" | "infinite"
    this.totalPages;
    this.totalProducts;
    this.pageSize;
    this.isVariantPagination;
    this.variantProductNodes;
    this.infiniteLoadingObserver;

    this.showColSwitchers;
    this.cachedCol;
    this.initialCol;
    this.activeCol;
    this.STORAGE_KEY = "gridColumnViews";
    this.MAX_COL_BY_SCREEN_SIZE = {
      mobile: 2,
      tablet: 4,
      desktop: 4,
    };

    this.enableFilters;
    this.enableSorting;
    this.filtersType; // "storefront_filters" | "tags_filter"
    this.filtersPosition; // "leftColumn" | "rightColumn" | "fixed"
    this.accordions;
    this.sideEffectEventsAdded = false;
  }

  init = () => {
    this.domNodes = queryDomNodes(this.selectors);
    this.mbSortingNodes = queryDomNodes(this.mobileSortingSelectors);
    this.setData();
    this.initGridView();
    this.initFilters();
    this.initMobileSorting();
    this.initProducts();
    this.sideEffectEventsAdded = true;
    MinimogEvents.subscribe("ON_PRODUCT_LIST_UPDATED", () => refreshProductReview());

    // Handle sticky toolbar on mobile
    this.handleStickyToolbar();
    document.addEventListener("matchMobile", () => this.handleStickyToolbar());
    document.addEventListener("unmatchMobile", () => this.handleStickyToolbar());
  };

  setData = () => {
    const { section, productContainer } = this.domNodes;
    // Grid view data
    this.sectionId = section.dataset.sectionId;
    this.paginationType = section.dataset.paginationType;
    this.totalPages = productContainer && Number(productContainer.dataset.totalPages);
    this.totalProducts = productContainer && Number(productContainer.dataset.totalItems);
    this.pageSize = productContainer && Number(productContainer.dataset.pageSize);
    this.isVariantPagination = productContainer && productContainer.dataset.variantPagination === "true";
    this.view = section.dataset.view;
    this.activeCol = null;
    this.currentPage = this.isVariantPagination ? this.getPageFromUrl() : 1;
    this.initialCol = Math.min(Number(section.dataset.initialColumn) || this.MAX_COL_BY_SCREEN_SIZE.desktop, this.MAX_COL_BY_SCREEN_SIZE.desktop);
    this.cachedCol = Number(window.localStorage.getItem(this.STORAGE_KEY)) || this.initialCol;
    this.cachedCol = Math.min(this.cachedCol, this.MAX_COL_BY_SCREEN_SIZE.desktop);
    this.showColSwitchers = section.dataset.showColSwitchers === "true";
    // Filters data
    this.enableFilters = section.dataset.enableFilters === "true";
    this.enableSorting = section.dataset.enableSorting === "true";
    this.filtersType = section.dataset.filtersType;
    this.filtersPosition = section.dataset.filtersPosition;
  };

  initGridView = () => {
    // Se o switcher de colunas estiver desativado, sempre usa o valor configurado no tema
    this.toggleView(this.isDesignMode || !this.showColSwitchers ? this.initialCol : this.cachedCol);
    this.updateViewByScreen();
    addEventDelegate({
      selector: this.selectors.cols[0],
      context: this.domNodes.toolbar,
      handler: (e, colNode) => this.toggleView(Number(colNode.dataset.column)),
    });
    if (this.isVariantPagination) {
      this.initVariantPagination();
    } else {
      this.initLoadMore();
    }
    if (!this.sideEffectEventsAdded) {
      window.addEventListener("resize", debounce(this.updateViewByScreen, 500));
    }
  };

  initFilters = () => {
    if (!this.enableFilters) return;
    const { sidebar, openSidebar, closeSidebar } = this.domNodes;
    window.requestAnimationFrame(this.initAccordions);
    openSidebar.addEventListener("click", this.openSidebarFilter);
    closeSidebar.addEventListener("click", this.closeSidebarFilter);
    sidebar.addEventListener("click", (e) => e.target === sidebar && this.closeSidebarFilter());
  };

  initMobileSorting = () => {
    if (!this.enableSorting) return;
    const { openSorting, closeSorting, sortingWrapper } = this.mbSortingNodes;
    openSorting.addEventListener("click", this.openMobileSorting);
    closeSorting.addEventListener("click", this.closeMobileSorting);
    sortingWrapper.addEventListener("click", (e) => e.target === sortingWrapper && this.closeMobileSorting());
  };

  openMobileSorting = () => {
    const { sortingWrapper, sortingList } = this.mbSortingNodes;
    sortingWrapper.style.display = "block";
    window.requestAnimationFrame(() => {
      sortingWrapper.style.setProperty("--m-bg-opacity", "0.5");
      sortingList.style.setProperty("--m-translate-y", "0");
    });
  };

  closeMobileSorting = (e) => {
    const { sortingWrapper, sortingList } = this.mbSortingNodes;
    sortingWrapper.style.setProperty("--m-bg-opacity", "0");
    sortingList.style.setProperty("--m-translate-y", "100%");
    setTimeout(() => sortingWrapper.style.setProperty("display", "none"), 300);
  };

  initAccordions = () => {
    this.accordions && this.accordions.destroy();
    const { filtersWrapper } = this.domNodes;
    filtersWrapper.classList.remove("acc-initialized");
    this.accordions = new MinimogLibs.Accordion(filtersWrapper, {
      presetContentHeight: window.innerWidth > 1280 && this.filtersPosition !== "fixed",
      callback: () => filtersWrapper.style.setProperty('opacity', '1')
    });
  };

  openSidebarFilter = () => {
    const { sidebar, sidebarContent, section } = this.domNodes;
    sidebar.style.display = "block";
    window.requestAnimationFrame(() => {
      sidebar.style.setProperty("--m-bg-opacity", "0.5");
      sidebarContent.style.setProperty("--m-translate-x", "0");
      this.accordions && this.accordions.setContentHeight();
    });
    section.classList.add("sidebar-open");
  };

  closeSidebarFilter = () => {
    const { sidebar, sidebarContent, section } = this.domNodes;
    section.classList.remove("sidebar-open");
    if (window.innerWidth < 1280 || sidebar.dataset.type === "fixed") {
      sidebarContent.style.setProperty("--m-translate-x", "-100%");
      sidebar.style.removeProperty("--m-bg-opacity");
      setTimeout(() => sidebar.style.removeProperty("display"), 300);
    }
  };

  toggleView = (col) => {
    col = Math.min(col, this.MAX_COL_BY_SCREEN_SIZE.desktop);
    if (!col || this.view === "search") return;
    const { cols, productContainer } = this.domNodes;
    if (!productContainer) return;
    // Resize image product card
    const allImages = productContainer.querySelectorAll("responsive-image");
    if (allImages && allImages.length > 0) {
      allImages.forEach((img) => {
        img.disconnectedCallback();
        img.connectedCallback();
      });
    }

    const activeCol = this.activeCol || this.initialCol;
    if (this.showColSwitchers) {
      const colNodes = Array.from(cols);
      const activeColNode = colNodes.find((node) => Number(node.dataset.column) === activeCol);
      const nextColNode = colNodes.find((node) => Number(node.dataset.column) === col);
      activeColNode && activeColNode.classList.remove("active");
      nextColNode && nextColNode.classList.add("active");
    }

    productContainer.classList.remove("m-cols-1", "m-cols-2", "m-cols-3", "m-cols-4", "m-cols-5");
    productContainer.classList.add(`m-cols-${col}`);

    window.localStorage.setItem(this.STORAGE_KEY, col);
    this.activeCol = col;
  };

  updateViewByScreen = () => {
    if (this.activeCol === 1) return; // List layout
    const device = this.getDeviceByScreenSize();
    const maxCol = this.MAX_COL_BY_SCREEN_SIZE[device];
    if (maxCol < this.activeCol) this.toggleView(maxCol);
  };

  getDeviceByScreenSize = () => {
    if (window.innerWidth < 768) return "mobile";
    if (window.innerWidth < 1024) return "tablet";
    return "desktop";
  };

  getPageFromUrl = () => {
    const page = Number(new URLSearchParams(window.location.search).get("page")) || 1;
    return Math.max(1, Math.min(page, this.totalPages || 1));
  };

  getVariantPageUrl = (page) => {
    const url = new URL(window.location.href);
    if (page <= 1) {
      url.searchParams.delete("page");
    } else {
      url.searchParams.set("page", page);
    }
    return `${url.pathname}${url.search}${url.hash}`;
  };

  updateVariantPageUrl = (page) => {
    window.history.pushState({}, "", this.getVariantPageUrl(page));
  };

  initVariantPagination = () => {
    const { productContainer, loadMoreBtn, variantPaginationNav } = this.domNodes;
    if (!productContainer) return;

    this.variantProductNodes = Array.from(productContainer.querySelectorAll("[data-variant-card-index]"));
    this.totalPages = Math.max(1, Number(productContainer.dataset.totalPages) || 1);
    this.pageSize = Number(productContainer.dataset.pageSize) || this.variantProductNodes.length || 1;

    if (this.paginationType === "paginate") {
      this.currentPage = this.getPageFromUrl();
      this.renderVariantPaginationNav();
      this.showVariantPage(this.currentPage, false);
      return;
    }

    this.currentPage = 1;
    this.showVariantItemsThroughPage(this.currentPage);

    if (!loadMoreBtn || this.totalPages <= 1) return;
    loadMoreBtn.addEventListener("click", this.loadMoreVariantProducts);

    if (this.paginationType === "infinite") {
      this.infiniteLoadingObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio === 1) this.loadMoreVariantProducts();
          });
        },
        { threshold: 1 }
      );

      this.infiniteLoadingObserver.observe(loadMoreBtn);
    }

    if (!variantPaginationNav && this.currentPage >= this.totalPages) {
      loadMoreBtn.parentNode.remove();
    }
  };

  renderVariantPaginationNav = () => {
    const { variantPaginationNav } = this.domNodes;
    if (!variantPaginationNav || this.totalPages <= 1) return;

    const parts = [];
    const windowSize = 2;
    const start = Math.max(1, this.currentPage - windowSize);
    const end = Math.min(this.totalPages, this.currentPage + windowSize);

    if (this.currentPage > 1) {
      parts.push(`<span class="prev"><a href="${this.getVariantPageUrl(this.currentPage - 1)}" data-variant-page="${this.currentPage - 1}" title="">&laquo;</a></span>`);
    }

    if (start > 1) {
      parts.push(this.getVariantPagePart(1));
      if (start > 2) parts.push('<span class="deco">&hellip;</span>');
    }

    for (let page = start; page <= end; page += 1) {
      parts.push(this.getVariantPagePart(page));
    }

    if (end < this.totalPages) {
      if (end < this.totalPages - 1) parts.push('<span class="deco">&hellip;</span>');
      parts.push(this.getVariantPagePart(this.totalPages));
    }

    if (this.currentPage < this.totalPages) {
      parts.push(`<span class="next"><a href="${this.getVariantPageUrl(this.currentPage + 1)}" data-variant-page="${this.currentPage + 1}" title="">${this.getNextPaginationIcon()}</a></span>`);
    }

    variantPaginationNav.innerHTML = parts.join(" ");
    variantPaginationNav.onclick = (event) => {
      const pageLink = event.target.closest("[data-variant-page]");
      if (!pageLink) return;
      event.preventDefault();
      this.showVariantPage(Number(pageLink.dataset.variantPage), true);
    };
  };

  getVariantPagePart = (page) => {
    if (page === this.currentPage) {
      return `<span class="page current">${page}</span>`;
    }
    return `<span class="page"><a href="${this.getVariantPageUrl(page)}" data-variant-page="${page}" title="">${page}</a></span>`;
  };

  getNextPaginationIcon = () => {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="9" height="16" viewBox="0 0 9 16" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M7.93899 8.48472L1.58757 15.2727L0 13.576L5.55763 7.63636L0 1.6967L1.58757 0L7.93899 6.78801C8.14947 7.01303 8.26772 7.31818 8.26772 7.63636C8.26772 7.95454 8.14947 8.2597 7.93899 8.48472Z" fill="#A7A7A7"/></svg>';
  };

  showVariantPage = (page, updateUrl = true) => {
    this.currentPage = Math.max(1, Math.min(Number(page) || 1, this.totalPages || 1));
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;

    this.variantProductNodes.forEach((node, index) => {
      node.style.display = index >= start && index < end ? "" : "none";
    });

    if (updateUrl) this.updateVariantPageUrl(this.currentPage);
    this.renderVariantPaginationNav();
    this.initProducts();
    MinimogEvents.emit("ON_PRODUCT_LIST_UPDATED");
  };

  showVariantItemsThroughPage = (page) => {
    this.currentPage = Math.max(1, Math.min(Number(page) || 1, this.totalPages || 1));
    const visibleItems = this.currentPage * this.pageSize;

    this.variantProductNodes.forEach((node, index) => {
      node.style.display = index < visibleItems ? "" : "none";
    });

    const { loadMoreBtn } = this.domNodes;
    if (loadMoreBtn && this.currentPage >= this.totalPages) {
      loadMoreBtn.parentNode.remove();
      this.infiniteLoadingObserver && this.infiniteLoadingObserver.unobserve(loadMoreBtn);
    }

    this.initProducts();
    MinimogEvents.emit("ON_PRODUCT_LIST_UPDATED");
  };

  loadMoreVariantProducts = () => {
    if (this.currentPage >= this.totalPages) return;
    this.showVariantItemsThroughPage(this.currentPage + 1);
  };

  initLoadMore = () => {
    if (this.paginationType === "paginate" || this.totalPages <= 1 || this.view === "search") return;

    const { loadMoreBtn } = this.domNodes;
    loadMoreBtn.addEventListener("click", this.loadMoreProducts);

    if (this.paginationType === "infinite") {
      this.infiniteLoadingObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio === 1) this.loadMoreProducts();
          });
        },
        { threshold: 1 }
      );

      this.infiniteLoadingObserver.observe(loadMoreBtn);
    }
  };

  loadMoreProducts = () => {
    const nextPage = this.currentPage + 1;
    if (nextPage > this.totalPages) return;

    const { productContainer, loadMoreBtn } = this.domNodes;
    this.toggleLoading(true);
    fetchSection(this.sectionId, { fromCache: true, params: { page: nextPage } })
      .then((productGridHTML) => {
        const productNodes = productGridHTML.querySelectorAll("[data-product-container] .m-product-item");
        productNodes.forEach((prodNode) => productContainer.appendChild(prodNode));
        this.initProducts();
      })
      .catch((err) => console.error(`Failed to load more products.`, err))
      .finally(() => {
        MinimogEvents.emit("ON_PRODUCT_LIST_UPDATED");
        this.toggleLoading(false);
        this.currentPage = nextPage;
        if (nextPage >= this.totalPages) {
          loadMoreBtn.parentNode.remove();
          this.infiniteLoadingObserver && this.infiniteLoadingObserver.unobserve(loadMoreBtn);
        }
      });
  };

  initProducts = () => {
    MinimogTheme.CompareProduct && MinimogTheme.CompareProduct.setCompareButtonsState();
    MinimogTheme.Wishlist && MinimogTheme.Wishlist.setWishlistButtonsState();
  };

  toggleLoading = (loading) => {
    const { loadMoreBtn } = this.domNodes;
    if (loadMoreBtn) {
      const method = loading ? "add" : "remove";
      loadMoreBtn.classList[method]("m-spinner-loading");
    }
  };

  handleStickyToolbar() {
    const toolbar = this.domNodes.toolbar;
    const extraSpace = toolbar && toolbar.offsetTop;
    let lastScroll = 0;
    window.addEventListener("scroll", () => {
      if (MinimogTheme.config.mqlMobile) {
        const currentScroll = window.scrollY;
        if (currentScroll <= extraSpace) {
          toolbar.classList.remove("scroll-up", "m-collection-toolbar--sticky");
          return;
        }
        toolbar.classList.add("m-collection-toolbar--sticky");
        if (
          currentScroll > toolbar.offsetHeight + extraSpace &&
          currentScroll > lastScroll &&
          !toolbar.classList.contains("scroll-down")
        ) {
          toolbar.classList.remove("scroll-up");
          toolbar.classList.add("scroll-down");
        } else if (currentScroll < lastScroll && toolbar.classList.contains("scroll-down")) {
          toolbar.classList.remove("scroll-down");
          toolbar.classList.add("scroll-up");
        }
        lastScroll = currentScroll;
      } else {
        toolbar.classList.remove("scroll-up", "m-collection-toolbar--sticky", "scroll-down");
      }
    });
  }
}
